import React, { useState, useMemo } from 'react';
import { SearchBox, List, Checkbox, mergeStyleSets } from '@fluentui/react';
import { includesParttern, MergeListsAction, mergeLists } from 'shared/utils/utils';


const classNames = mergeStyleSets({
  fields: {
    border: '1px solid #A6A6A6',
  },
  fieldsSearch: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderBottom: '1px solid #A6A6A6',
    selectors: {
      '& > :first-child': {
        marginLeft: 8,
      },
      '& > :last-child': {
        flexGrow: 1,
        border: 0,
      },
    },
  },
  fieldsList: {
    margin: '8px 0px 0px 0px',
    maxHeight: '40vh',
    minHeight: 24,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  fieldsListItem: {
    display: 'flex',
    width: '100%',
    padding: '2px 8px',
    cursor: 'pointer',
    alignItems: 'baseline',
    selectors: {
      '&:hover': {
        backgroundColor: 'lightgrey',
      },
    },
  },
});


export const CheckboxListWithSearch = ({ filterPlaceholder = '', areAllChecked = false,
  listItems = [], onAllChecked, onItemChecked, onFilterChanged, disabled }) => {
  return (
    <div className={classNames.fields}>
      <div className={classNames.fieldsSearch}>
        <Checkbox
          checked={areAllChecked}
          disabled={disabled}
          onChange={(_, checked) => onAllChecked?.(checked) }
        />
        <SearchBox
          placeholder={filterPlaceholder}
          onChange={event => onFilterChanged?.(event?.target?.value ?? '') }
          onClear={() => onFilterChanged?.('') }
          iconProps={{ iconName: 'Filter' }}
        />
      </div>

      <List
        className={classNames.fieldsList}
        items={listItems}
        onRenderCell={(item, index) => (
          <Checkbox
            className={classNames.fieldsListItem}
            label={item.text}
            checked={item.selected}
            disabled={disabled}
            onChange={(event, checked) => { onItemChecked?.(checked, item) }}
          />
        )}
      />
    </div>
  );
}


const idEq = (a, b) => a.id === b.id;
const getMergeAction = (areChecked) => areChecked 
  ? MergeListsAction.Merge 
  : MergeListsAction.Remove;


export function useCheckboxFiltering({ filter = '', items = [], onItemsChecked }) {
  const visibleItems = useMemo(() => filter
    ? items.filter(x => includesParttern(x.text, filter))
    : items
  , [items, filter]);

  const areAllChecked = useMemo(() => visibleItems.every(x => x.selected), [visibleItems]);
  const allChecked = useMemo(() => items.filter(x => x.selected), [items]);

  // console.log('useCheckbox: ', areAllChecked, visibleItems, allChecked);

  function handleAllChecked(areChecked) {
    const checked = visibleItems.map(x => ({ ...x, selected: areChecked }));
    const allSelected = !filter
      ? areChecked ? items : []
      : mergeLists(allChecked, checked, getMergeAction(areChecked), idEq)
    onItemsChecked(areChecked, checked, allSelected);
  }

  function handleItemChecked(isChecked, item) {
    onItemsChecked(isChecked, [item], mergeLists(allChecked, [item], getMergeAction(isChecked), idEq))
  }

  return {
    items,
    visibleItems,
    areAllChecked,
    onAllChecked: handleAllChecked,
    onItemChecked: handleItemChecked,
  };
}


export function useCheckboxFilteringState(props) {
  const { filter: iFilter = '' } = props;
  const [filter, setFilter] = useState(iFilter);

  return {
    onFilterChanged: setFilter,
    filter,
    ...useCheckboxFiltering({ ...props, filter })
  };
}



export default CheckboxListWithSearch;